'use client';
import { trackNaverLead } from '@/components/naver-wcs';
import SearchSelect from '@/components/search-select';
import { Z_INDEX } from '@/constants';
import { trackGaEvent } from '@/lib/analytics';
import { getAttributionPayload } from '@/lib/attribution';
import { useStoreReserve } from '@/services/mutation/store.mutation';
import { useRegionList } from '@/services/query/region.query';
import { useStoreList } from '@/services/query/store.query';
import { SiteCategory } from '@/services/types/globals.type';
import { RegionEntity } from '@/services/types/region.type';
import { StoreEntity } from '@/services/types/store.type';
import { useModalStore } from '@/store/modal.store';
import CheckboxChecked from '@/svg/checkbox_checked.svg';
import CheckboxUnchecked from '@/svg/checkbox_unchecked.svg';
import NoticeIcon from '@/svg/notice.svg';
import { zodResolver } from '@hookform/resolvers/zod';
import { X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
// Define the form schema with Zod
const formSchema = z.object({
  region: z.string().min(1, '지역을 선택해주세요'),
  branch: z.string().min(1, '지점을 선택해주세요'),
  name: z
    .string()
    .min(2, '이름을 2자 이상 입력해주세요')
    .max(20, '이름을 20자 이하로 입력해주세요')
    .regex(/^[가-힣a-zA-Z\s]+$/, '이름은 한글 또는 영문만 입력 가능합니다'),
  contact: z
    .string()
    .min(10, '연락처를 정확히 입력해주세요')
    .max(13, '연락처를 정확히 입력해주세요')
    .regex(/^01[016789]-?\d{3,4}-?\d{4}$/, '연락처 형식이 올바르지 않습니다'),
  inquiryType: z.enum(['2종', '1종', '대형', '장롱'], {
    required_error: '문의 유형을 선택해주세요',
  }),
  privacyPolicyAccepted: z.boolean().refine((val) => val === true, {
    message: '개인정보 수집 이용에 동의해주세요',
  }),
});
// Infer the type from the schema
type FormValues = z.infer<typeof formSchema>;

type BranchSelectType = StoreEntity & { label: string; value: string };

export default function ConsultationForm() {
  const { close, selectedBranch, reserveContext } = useModalStore();
  const [showTerms, setShowTerms] = useState(false);

  // 모달 닫기 함수 (history 정리 포함)
  const handleClose = useCallback(() => {
    // history에서 모달 상태 제거
    if (window.history.state?.modal) {
      window.history.back();
    }
    close();
  }, [close]);

  // 시/도, 지점 상태 추가
  const { data: RegionList } = useRegionList();

  const siDoRegions = RegionList?.data || [];
  const [province, setProvince] = useState<RegionEntity | undefined>();
  const [branch, setBranch] = useState<BranchSelectType | undefined>();

  // 시/도 선택 시 해당 학원 리스트 조회
  const { data: storeListData } = useStoreList({
    province: province?.value,
    limit: 9999,
  });
  const storeList = storeListData?.data?.stores || [];

  const storeReserve = useStoreReserve();

  const defaultValues = {
    region: '',
    branch: '',
    name: '',
    contact: '',
    inquiryType: '장롱',
    privacyPolicyAccepted: false,
  } as FormValues;
  // Initialize the form with React Hook Form and Zod resolver
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues,
    mode: 'onChange', // Validate on change to enable/disable submit button
  });

  // Get form state to check validity
  const { isSubmitting, isValid } = form.formState;

  // 버튼 활성화 조건
  const isButtonDisabled = !isValid || isSubmitting;

  // Form submission handler
  const onSubmit = (data: FormValues) => {
    if (!branch || !branch.id) {
      toast('지점을 올바르게 선택해주세요.', {
        position: 'top-center',
        style: {
          color: '#ffffff',
        },
        className: 'text-white [&>div]:text-white [&>div>div]:text-white',
        descriptionClassName: '!text-[#999999]',
      });
      return;
    }
    const bodyData = {
      storeId: branch.id,
      category: SiteCategory.TRAINING,
      inflowType: 'web',
      realname: data.name,
      phone: data.contact,
      license: data.inquiryType,
      consultType: data.inquiryType,
      memo: '',
      // 요금제 카드에서 연 경우 선택한 요금제·진입 경로를 함께 싣는다
      pricingPlanId: reserveContext?.pricingPlanId,
      pricingPlanPriceId: reserveContext?.pricingPlanPriceId,
      entrySource: reserveContext?.entrySource,
      // 처음 유입·신청 직전 유입·신청 페이지(lib/attribution)
      attribution: getAttributionPayload(),
    };
    storeReserve.mutate(bodyData, {
      onSuccess: () => {
        // GA4 키 이벤트: 상담 신청 완료 (개인정보 없이 지점·문의 유형·진입 경로만)
        // 네이버 광고 잠재고객 전환: 상담 신청 접수
        trackNaverLead();
        trackGaEvent('generate_lead', {
          store_name: branch.name,
          consult_type: data.inquiryType,
          entry_source: reserveContext?.entrySource,
        });
        handleClose();
        toast('예약이 완료되었습니다.', {
          description: '빠른 시간안에 연락드리겠습니다.',
          duration: 3000,
          position: 'top-center',
          style: {
            color: '#ffffff',
          },
          className: 'text-white [&>div]:text-white [&>div>div]:text-white',
          descriptionClassName: '!text-[#999999]',
        });

        setProvince(undefined);
        setBranch(undefined);
        form.reset(defaultValues);
      },
      onError: () => {
        toast('예약에 실패했습니다. 다시 시도해주세요.', {
          position: 'top-center',
          style: {
            color: '#ffffff',
          },
          className: 'text-white [&>div]:text-white [&>div>div]:text-white',
          descriptionClassName: '!text-[#999999]',
        });
      },
    });
  };

  // Prevent body scrolling when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      handleClose();
    };

    // 모달이 열릴 때 history에 상태 추가
    window.history.pushState({ modal: true }, '');

    // popstate 이벤트 리스너 추가
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handleClose]);

  // selectedBranch가 있을 때 초기셋팅 적용
  useEffect(() => {
    if (selectedBranch) {
      // 해당 지점의 지역 정보 찾기
      const branchRegion = siDoRegions.find((region) =>
        selectedBranch.address.startsWith(region.value),
      );

      if (branchRegion) {
        setProvince(branchRegion);

        // 지점 정보 설정
        const branchData: BranchSelectType = {
          ...selectedBranch,
          label: selectedBranch.name,
          value: selectedBranch.id.toString(),
        };
        setBranch(branchData);

        // 폼 값 업데이트
        form.setValue('region', branchRegion.value);
        form.setValue('branch', selectedBranch.id.toString());
      }
    }
  }, [selectedBranch, siDoRegions, form]);

  return (
    <div
      className="fixed inset-0 top-0 flex items-center justify-center overflow-y-auto"
      style={{
        zIndex: Z_INDEX.Modal,
      }}
    >
      {/* Backdrop - only visible on desktop */}
      {/* <div className="absolute inset-0 block bg-black/40 backdrop-blur-sm" /> */}
      <div
        className="absolute inset-0 block bg-black/40"
        onClick={handleClose}
      />

      <div
        className="desktop:max-h-[696px] desktop:max-w-md desktop:overflow-hidden desktop:rounded-2xl desktop:shadow-lg relative flex w-full max-w-[500px] flex-col bg-white"
        style={{ height: 'var(--app-height, 100vh)' }}
      >
        <div
          className="border-stroke-white desktop:border-0 desktop:py-6 sticky top-0 z-10 flex items-center justify-between border-b-1 bg-white px-6 py-6"
          style={{
            zIndex: Z_INDEX.Modal,
          }}
        >
          <h2 className="typo-28 text-level-2 font-extrabold">상담 예약하기</h2>
          <button className="text-black" onClick={handleClose}>
            <X
              size={32}
              strokeWidth={1.5}
              className="desktop:hidden cursor-pointer"
            />
            <X
              size={32}
              strokeWidth={2.5}
              className="desktop:block hidden cursor-pointer"
            />
          </button>
        </div>
        <hr className="text-stroke-white h-[1px]" />

        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="desktop:pt-7 desktop:overflow-hidden desktop:min-h-0 flex min-h-full flex-1 flex-col gap-7 overflow-y-auto px-6 pt-7 pb-10"
          style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
        >
          <div className="grid grid-cols-2 gap-3">
            <Controller
              control={form.control}
              name="region"
              render={({ field }) => (
                <SearchSelect
                  triggerPlaceholder="시/도 선택"
                  selected={province ? [province] : []}
                  onFilterChange={(selected) => {
                    const region = selected as RegionEntity;
                    field.onChange(region.value);
                    setProvince(region);
                    form.setValue('branch', '');
                    setBranch(undefined);
                  }}
                  list={siDoRegions}
                  triggerAddClass="!h-[66px]"
                />
              )}
            />
            <Controller
              control={form.control}
              name="branch"
              render={({ field }) => (
                <SearchSelect
                  triggerPlaceholder="지점"
                  selected={branch ? [branch] : []}
                  onFilterChange={(selected) => {
                    field.onChange(selected.label); // 지점명
                    setBranch(selected as BranchSelectType); // 타입 캐스팅
                  }}
                  list={storeList.map((store) => ({
                    ...store,
                    label: store.name,
                    value: String(store.id),
                  }))}
                  triggerAddClass="!h-[66px]"
                />
              )}
            />
          </div>

          {reserveContext?.pricingLabel && (
            <div className="bg-bg-grayblue flex items-center gap-2 rounded-md px-4 py-3">
              <span className="typo-14 text-level-5 shrink-0 font-semibold">
                선택한 요금제
              </span>
              <span className="typo-16 text-level-2 truncate font-bold">
                {reserveContext.pricingLabel}
              </span>
            </div>
          )}

          <div>
            <h3 className="typo-20 mb-4 font-bold">예약자 정보</h3>
            <div className="space-y-2">
              <input
                {...form.register('name')}
                placeholder="이름을 입력해주세요."
                className="border-stroke-white focus:border-stroke-graylight placeholder:text-level-6 placeholder:typo-18 h-14 w-full rounded-md border px-6 py-2 placeholder:font-semibold focus:ring-0 focus:outline-none"
              />
              {form.formState.errors.name && (
                <p className="mt-1 text-sm text-red-500">
                  {form.formState.errors.name.message as string}
                </p>
              )}
              <input
                {...form.register('contact')}
                placeholder="연락처를 입력해주세요."
                className="border-stroke-white focus:border-stroke-graylight placeholder:text-level-6 placeholder:typo-18 h-14 w-full rounded-md border px-6 py-2 placeholder:font-semibold focus:ring-0 focus:outline-none"
              />
              {form.formState.errors.contact && (
                <p className="mt-1 text-sm text-red-500">
                  {form.formState.errors.contact.message as string}
                </p>
              )}
            </div>
          </div>
          {/*
          <div>
            <h3 className="typo-20 mb-4 font-bold">문의유형</h3>
            <Controller
              control={form.control}
              name="inquiryType"
              render={({ field }) => (
                <div className="grid grid-cols-4 gap-2">
                  {['2종', '1종', '대형', '장롱'].map((type) => (
                    <button
                      type="button"
                      key={type}
                      className={`typo-20 h-14 cursor-pointer rounded-md ${
                        field.value === type
                          ? 'text-level-2 bg-main-yellow font-bold'
                          : 'bg-bg-grayblue text-font-grayblue font-semibold'
                      }`}
                      onClick={() => field.onChange(type)}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              )}
            />
          </div> */}

          <div className="bg-bg-graylight rounded-md px-5 py-5">
            <div className="flex gap-1.5">
              <NoticeIcon className="mt-0.5 size-4 flex-shrink-0" />
              <p className="typo-16 text-level-4 leading-snug font-semibold">
                <span className="desktop:hidden">
                  작성해주신 연락처로 예약 안내가 이뤄집니다. 빠른 시간 안에
                  연락드리겠습니다.
                </span>
                <span className="desktop:block hidden">
                  작성해주신 연락처로 예약 안내가 이뤄집니다. <br />
                  빠른 시간 안에 연락드리겠습니다.
                </span>
              </p>
            </div>
          </div>

          <div className="h-1"></div>

          <div className="border-stroke-white desktop:mt-0 desktop:pt-7 border-t-1 pt-7">
            <div className="mb-5 flex grow">
              <Controller
                control={form.control}
                name="privacyPolicyAccepted"
                render={({ field }) => (
                  <div className="flex grow flex-row items-center justify-between">
                    <button
                      type="button"
                      className="flex size-5 cursor-pointer items-center justify-center rounded border-none bg-transparent p-0"
                      onClick={() => field.onChange(!field.value)}
                    >
                      {field.value ? (
                        <CheckboxChecked className="size-5" />
                      ) : (
                        <CheckboxUnchecked className="size-5" />
                      )}
                    </button>
                    <div className="ml-1 flex flex-1 cursor-pointer items-center justify-between">
                      <span
                        className="typo-16 text-level-1 font-semibold"
                        onClick={() => field.onChange(!field.value)}
                      >
                        (필수) 개인정보 수집 이용에 동의합니다.
                      </span>
                      <button
                        type="button"
                        className="typo-16 text-level-5 cursor-pointer font-semibold underline"
                        onClick={() => setShowTerms(!showTerms)}
                      >
                        보기
                      </button>
                    </div>
                  </div>
                )}
              />
            </div>

            {/* Terms and conditions container - only visible when showTerms is true */}
            {showTerms && (
              <div className="border-stroke-white mb-4 overflow-hidden rounded-md border">
                <div className="h-32 overflow-y-auto bg-gray-50 p-3 text-sm text-gray-600">
                  <h4 className="mb-2 font-medium">
                    개인정보 수집 및 이용 동의
                  </h4>
                  <p className="mb-2">1. 수집항목: 이름, 연락처</p>
                  <p className="mb-2">
                    2. 수집 및 이용목적: 상담 예약 및 관련 서비스 제공
                  </p>
                  <p className="mb-2">3. 보유 및 이용기간: 수집일로부터 1년</p>
                  <p className="mb-2">
                    4. 동의를 거부할 권리: 개인정보 수집 이용에 대한 동의를
                    거부할 수 있으나, 동의 거부 시 상담 예약이 제한됩니다.
                  </p>
                  <p className="mb-2">
                    5. 개인정보 처리 위탁: 당사는 원활한 개인정보 업무처리를
                    위하여 다음과 같이 개인정보 처리업무를 위탁하고 있습니다.
                  </p>
                  <p>위와 같이 개인정보를 수집·이용하는데 동의하십니까?</p>
                </div>
              </div>
            )}

            {/* Submit button - fixed to bottom on mobile */}
            <div className={`bg-white`} id="consultation-button-wrapper">
              <div
                id="consultation-button"
                className={`typo-24 bg-main-blue flex h-15 w-full cursor-pointer items-center justify-center rounded-md font-extrabold text-white ${isButtonDisabled ? 'pointer-events-none opacity-50' : ''}`}
                role="button"
                tabIndex={isButtonDisabled ? -1 : 0}
                aria-disabled={isButtonDisabled}
                onClick={
                  isButtonDisabled ? undefined : form.handleSubmit(onSubmit)
                }
                onKeyDown={(e) => {
                  if (
                    (e.key === 'Enter' || e.key === ' ') &&
                    !isButtonDisabled
                  ) {
                    form.handleSubmit(onSubmit)();
                  }
                }}
              >
                예약하기
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
